/**
 * AL SULTAN — Mobile Motion Layer
 * Drift rails, scroll-driven trust numerals, gilded ticker,
 * header microinteractions, editorial navigation drawer.
 */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function clamp(v, min, max) {
    return v < min ? min : v > max ? max : v;
  }

  function smoothstep(t) {
    return t * t * (3 - 2 * t);
  }

  /* ─────────────────────────────────────────────────────────────
     1. DRIFT RAIL ENGINE
     Continuous, sub-pixel product drift for touch devices. The stage is
     moved with transforms (never scrollLeft) so the loop is seamless in
     both directions. Touch holds it, a horizontal drag moves it, a fling
     coasts with friction, and the drift eases back in after a pause.
     Vertical gestures are left to the browser via touch-action: pan-y.
  ───────────────────────────────────────────────────────────── */
  function DriftRail(options) {
    this.viewport = options.viewport;
    this.stage = options.stage;
    this.getItems = options.getItems;
    this.getLoopWidth = options.getLoopWidth;
    this.speed = options.speed || 28;

    this.offset = 0;
    this.vel = 0;
    this.loop = 0;
    this.base = 0;
    this.vw = 0;
    this.items = [];
    this.active = false;
    this.inView = false;
    this.holding = false;
    this.dragging = false;
    this.axis = null;
    this.pointerId = null;
    this.resumeAt = 0;
    this.dragEndAt = -1e9;
    this.samples = [];
    this.raf = 0;
    this.last = 0;

    this.tick = this.tick.bind(this);
    this.onDown = this.onDown.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onUp = this.onUp.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onLostCapture = this.onLostCapture.bind(this);
    this.onClickCapture = this.onClickCapture.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onVisibility = this.onVisibility.bind(this);

    this.viewport.classList.add('als-drift-viewport');
    this.viewport.addEventListener('pointerdown', this.onDown);
    this.viewport.addEventListener('pointermove', this.onMove);
    this.viewport.addEventListener('pointerup', this.onUp);
    this.viewport.addEventListener('pointercancel', this.onCancel);
    this.viewport.addEventListener('lostpointercapture', this.onLostCapture);
    this.viewport.addEventListener('click', this.onClickCapture, true);
    this.viewport.addEventListener('dragstart', function (e) { e.preventDefault(); });
    document.addEventListener('visibilitychange', this.onVisibility);
    if (window.ResizeObserver) {
      new ResizeObserver(this.onResize).observe(this.viewport);
    } else {
      window.addEventListener('resize', this.onResize, { passive: true });
    }

    var self = this;
    this.io = new IntersectionObserver(function (entries) {
      self.inView = entries[entries.length - 1].isIntersecting;
      self.schedule();
    }, { rootMargin: '120px 0px' });
    this.io.observe(this.viewport);
  }

  DriftRail.prototype.start = function (opts) {
    var reset = opts && opts.reset;
    this.active = true;
    if (reset) {
      this.offset = 0;
      this.vel = 0;
    }
    var self = this;
    requestAnimationFrame(function () {
      self.measure();
      self.render();
      self.schedule();
    });
  };

  DriftRail.prototype.stop = function () {
    this.active = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.stage.style.transform = '';
  };

  DriftRail.prototype.measure = function () {
    var vpRect = this.viewport.getBoundingClientRect();
    var stageRect = this.stage.getBoundingClientRect();
    this.vw = vpRect.width;
    this.base = stageRect.left - vpRect.left - this.offset;
    this.loop = this.getLoopWidth() || 0;
    this.measuredWidth = vpRect.width;

    var items = this.getItems();
    var list = [];
    for (var i = 0; i < items.length; i++) {
      var r = items[i].getBoundingClientRect();
      list.push({ el: items[i], center: r.left - stageRect.left + r.width / 2, f: -1, p: -9 });
    }
    this.items = list;
    this.wrap();
  };

  DriftRail.prototype.onResize = function () {
    if (!this.active || Math.abs(this.viewport.getBoundingClientRect().width - this.measuredWidth) < 0.5) return;
    this.measure();
    this.render();
  };

  DriftRail.prototype.onVisibility = function () {
    this.last = 0;
    this.schedule();
  };

  DriftRail.prototype.schedule = function () {
    if (this.active && this.inView && !document.hidden && !this.raf) {
      this.last = 0;
      this.raf = requestAnimationFrame(this.tick);
    }
  };

  DriftRail.prototype.wrap = function () {
    if (this.loop <= 0) return;
    while (this.offset <= -this.loop) this.offset += this.loop;
    while (this.offset > 0) this.offset -= this.loop;
  };

  DriftRail.prototype.tick = function (now) {
    this.raf = 0;
    if (!this.active || !this.inView || document.hidden) return;

    var dt = this.last ? Math.min((now - this.last) / 1000, 0.05) : 0;
    this.last = now;

    if (!this.dragging) {
      if (this.holding) {
        this.vel *= Math.exp(-dt * 14);
      } else if (now < this.resumeAt) {
        this.vel *= Math.exp(-dt * 2.6);
      } else {
        this.vel += (-this.speed - this.vel) * (1 - Math.exp(-dt * 1.3));
      }
      this.offset += this.vel * dt;
      this.wrap();
      this.render();
    }

    this.raf = requestAnimationFrame(this.tick);
  };

  DriftRail.prototype.render = function () {
    this.stage.style.transform = 'translate3d(' + this.offset.toFixed(2) + 'px,0,0)';

    var half = this.vw / 2;
    var reach = this.vw * 0.62;
    for (var i = 0; i < this.items.length; i++) {
      var item = this.items[i];
      var x = this.base + this.offset + item.center;
      var p = (x - half) / reach;
      if (p < -1.8 || p > 1.8) {
        if (item.f !== 0) {
          item.f = 0;
          item.el.style.setProperty('--f', '0');
        }
        continue;
      }
      var f = smoothstep(clamp(1 - Math.abs(p), 0, 1));
      p = clamp(p, -1.4, 1.4);
      if (Math.abs(f - item.f) > 0.003) {
        item.f = f;
        item.el.style.setProperty('--f', f.toFixed(3));
      }
      if (Math.abs(p - item.p) > 0.003) {
        item.p = p;
        item.el.style.setProperty('--p', p.toFixed(3));
      }
    }
  };

  DriftRail.prototype.onDown = function (e) {
    if (!this.active || (e.pointerType === 'mouse' && e.button !== 0)) return;
    this.holding = true;
    this.dragging = false;
    this.axis = null;
    this.pointerId = e.pointerId;
    this.startX = this.lastX = e.clientX;
    this.startY = e.clientY;
    this.samples = [{ t: performance.now(), x: e.clientX }];
    this.viewport.classList.add('is-held');
    this.schedule();
  };

  DriftRail.prototype.onMove = function (e) {
    if (!this.holding || e.pointerId !== this.pointerId) return;
    var dx = e.clientX - this.startX;
    var dy = e.clientY - this.startY;

    if (this.axis === null) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      this.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (this.axis === 'y') {
        this.release(500);
        return;
      }
      this.dragging = true;
      this.viewport.classList.add('is-dragging');
      try { this.viewport.setPointerCapture(e.pointerId); } catch (err) { /* capture is optional */ }
    }

    if (!this.dragging) return;
    var now = performance.now();
    this.offset += e.clientX - this.lastX;
    this.lastX = e.clientX;
    this.samples.push({ t: now, x: e.clientX });
    while (this.samples.length > 2 && now - this.samples[0].t > 100) this.samples.shift();
    this.wrap();
    this.render();
  };

  DriftRail.prototype.onUp = function (e) {
    if (!this.holding || (e && e.pointerId !== undefined && e.pointerId !== this.pointerId)) return;
    if (this.dragging) {
      var s = this.samples;
      var first = s[0];
      var last = s[s.length - 1];
      var span = (last.t - first.t) / 1000;
      var v = span > 0.008 ? (last.x - first.x) / span : 0;
      if (performance.now() - last.t > 80) v = 0;
      this.vel = clamp(v, -2600, 2600);
      this.dragEndAt = performance.now();
      this.release(1900);
    } else {
      this.release(1500);
    }
  };

  // Taking capture for a drag makes the touched card lose its implicit
  // capture, and that event bubbles here. Only the viewport's own loss of
  // capture means the finger is gone; anything else would end every drag a
  // few pixels in and leave the rail coasting on its own.
  DriftRail.prototype.onLostCapture = function (e) {
    if (e.target !== this.viewport) return;
    this.onUp(e);
  };

  DriftRail.prototype.onCancel = function () {
    if (!this.holding) return;
    if (this.dragging) this.dragEndAt = performance.now();
    this.release(700);
  };

  DriftRail.prototype.release = function (delay) {
    this.holding = false;
    this.dragging = false;
    this.pointerId = null;
    this.resumeAt = performance.now() + delay;
    this.viewport.classList.remove('is-held', 'is-dragging');
    this.schedule();
  };

  DriftRail.prototype.onClickCapture = function (e) {
    if (performance.now() - this.dragEndAt < 400) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  window.AlsDriftRail = DriftRail;

  /* ─────────────────────────────────────────────────────────────
     2. WHY AL SULTAN — scroll-driven numerals
     Each numeral is mapped to its own distance from the viewport
     centre: it swells toward the reader, its gilded outline locks
     into register at the peak, then it recedes as the next arrives.
  ───────────────────────────────────────────────────────────── */
  function initWhyNumerals() {
    if (REDUCED) return;
    var mq = window.matchMedia('(max-width: 989px)');

    document.querySelectorAll('why-al-sultan').forEach(function (root) {
      if (root.dataset.alsNumerals) return;
      root.dataset.alsNumerals = '1';

      var groups = Array.prototype.slice.call(root.querySelectorAll('.why-al-sultan__group'));
      if (!groups.length) return;

      groups.forEach(function (g) {
        var n = g.querySelector('.why-al-sultan__bg-num');
        if (n) n.setAttribute('data-num', n.textContent.trim());
      });

      var enabled = false;
      var inView = false;
      var raf = 0;

      function update() {
        raf = 0;
        if (!enabled) return;
        var vh = window.innerHeight;
        var reach = vh * 0.34;
        // Phase 7: all reads before any write, so a scroll frame lays out once.
        var rects = groups.map(function (g) {
          return g.getBoundingClientRect();
        });
        for (var i = 0; i < groups.length; i++) {
          var r = rects[i];
          var t = clamp((r.top + r.height / 2 - vh * 0.5) / reach, -1.6, 1.6);
          var peak = smoothstep(clamp(1 - Math.abs(t), 0, 1));
          var prog = clamp((1.2 - t) / 2.4, 0, 1);
          var s = groups[i].style;
          s.setProperty('--als-t', t.toFixed(3));
          s.setProperty('--als-peak', peak.toFixed(3));
          s.setProperty('--als-prog', prog.toFixed(3));
          groups[i].classList.toggle('is-num-peak', peak > 0.7);
        }
      }

      function request() {
        if (enabled && inView && !raf) raf = requestAnimationFrame(update);
      }

      // In depth mode every description is visible, so expose them all.
      function syncA11y() {
        root.querySelectorAll('.why-al-sultan__item').forEach(function (item) {
          var open = enabled || item.classList.contains('is-active');
          item.setAttribute('aria-expanded', open ? 'true' : 'false');
          var desc = item.querySelector('.why-al-sultan__desc');
          if (desc) desc.setAttribute('aria-hidden', open ? 'false' : 'true');
        });
      }

      root.addEventListener('click', function () {
        if (enabled) window.setTimeout(syncA11y, 0);
      });

      function setMode() {
        enabled = mq.matches;
        root.classList.toggle('als-num-scroll', enabled);
        syncA11y();
        if (!enabled) {
          groups.forEach(function (g) {
            g.style.removeProperty('--als-t');
            g.style.removeProperty('--als-peak');
            g.style.removeProperty('--als-prog');
            g.classList.remove('is-num-peak');
          });
        }
        request();
      }

      new IntersectionObserver(function (entries) {
        inView = entries[entries.length - 1].isIntersecting;
        request();
      }, { rootMargin: '25% 0px' }).observe(root);

      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request, { passive: true });
      mq.addEventListener('change', setMode);
      setMode();
    });
  }

  /* ─────────────────────────────────────────────────────────────
     3. ANNOUNCEMENT TICKER — gilded ribbon
  ───────────────────────────────────────────────────────────── */
  function initTicker() {
    document.querySelectorAll('.sultan-ticker').forEach(function (ticker) {
      if (ticker.dataset.alsTicker) return;
      ticker.dataset.alsTicker = '1';
      ticker.classList.add('als-ticker');

      ticker.querySelectorAll('.sultan-ticker__item').forEach(function (item) {
        if (/FREE/i.test(item.textContent)) item.classList.add('als-ticker__promo');
      });

      if (REDUCED) return;

      ['als-ticker__sheen', 'als-ticker__rule'].forEach(function (cls) {
        var el = document.createElement('span');
        el.className = cls;
        el.setAttribute('aria-hidden', 'true');
        ticker.appendChild(el);
      });

      // Constant reading speed whatever the number of messages: one loop is one
      // group's width at ~40px/s (the speed the ribbon had with two messages).
      var track = ticker.querySelector('.sultan-ticker__track');
      var group = ticker.querySelector('.sultan-ticker__group');
      var lastDur = 0;
      function pace() {
        if (!track || !group) return;
        var dur = group.getBoundingClientRect().width / 40;
        if (dur > 0 && Math.abs(dur - lastDur) > lastDur * 0.05) {
          lastDur = dur;
          track.style.setProperty('--als-ticker-dur', dur.toFixed(1) + 's');
        }
      }
      // Phase 7: a ResizeObserver reports the group's width after layout (first frame, font
      // swap, rotation) without forcing a synchronous layout during start-up.
      if (window.ResizeObserver && group) {
        new ResizeObserver(pace).observe(group);
      } else {
        pace();
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(pace);
        window.addEventListener('resize', pace, { passive: true });
      }

      function hold() { ticker.classList.add('is-held'); }
      function letGo() { ticker.classList.remove('is-held'); }
      ticker.addEventListener('pointerdown', hold);
      ticker.addEventListener('pointerup', letGo);
      ticker.addEventListener('pointercancel', letGo);
      ticker.addEventListener('pointerleave', letGo);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     4. HEADER MICROINTERACTIONS
  ───────────────────────────────────────────────────────────── */
  function tapRipple(icon) {
    if (REDUCED || !icon) return;
    // Fixed layer: positioning inside the icon would re-anchor Dawn's drawer backdrop.
    var rect = icon.getBoundingClientRect();
    var ripple = document.createElement('span');
    ripple.className = 'als-icon-ripple';
    ripple.setAttribute('aria-hidden', 'true');
    ripple.style.left = rect.left + rect.width / 2 + 'px';
    ripple.style.top = rect.top + rect.height / 2 + 'px';
    document.body.appendChild(ripple);
    icon.classList.remove('als-tap');
    void icon.offsetWidth;
    icon.classList.add('als-tap');
    window.setTimeout(function () {
      ripple.remove();
      icon.classList.remove('als-tap');
    }, 620);
  }

  function initHeaderIcons() {
    if (document.documentElement.dataset.alsHeaderIcons) return;
    document.documentElement.dataset.alsHeaderIcons = '1';

    document.addEventListener('pointerdown', function (e) {
      var icon = e.target.closest && e.target.closest('.header__icon--menu, .header__icon--search, #cart-icon-bubble');
      if (icon) tapRipple(icon);
    }, { passive: true });

    // Animated close for the header search modal.
    customElements.whenDefined('details-modal').then(function () {
      var DM = customElements.get('details-modal');
      if (!DM || DM.prototype.__alsPatched) return;
      DM.prototype.__alsPatched = true;
      var originalClose = DM.prototype.close;
      DM.prototype.close = function (focusToggle) {
        var self = this;
        if (REDUCED || !this.classList.contains('header__search') || !this.detailsContainer || !this.detailsContainer.hasAttribute('open')) {
          return originalClose.call(this, focusToggle);
        }
        if (this.__alsClosing) return;
        this.__alsClosing = true;
        this.classList.add('is-closing');
        window.setTimeout(function () {
          self.classList.remove('is-closing');
          self.__alsClosing = false;
          originalClose.call(self, focusToggle);
        }, 300);
      };

      // The modal's close button is bound to the original close() in Dawn's
      // constructor, so route it through the animated close here.
      document.addEventListener('click', function (e) {
        var btn = e.target.closest && e.target.closest('details-modal.header__search .search-modal__close-button');
        if (!btn) return;
        e.preventDefault();
        e.stopPropagation();
        btn.closest('details-modal').close(false);
      }, true);
    });

    initCartCount();
  }

  /* Cart count odometer + gilded burst. Plays when the header icon is
     actually visible, so updates made behind the open cart drawer are
     celebrated the moment the drawer closes. */
  function initCartCount() {
    var icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;

    function readCount() {
      var el = document.querySelector('#cart-icon-bubble .cart-count-bubble span[aria-hidden="true"]');
      if (el && el.classList.contains('als-roll')) el = el.querySelector('.als-roll__in');
      var n = el ? parseInt(el.textContent, 10) : 0;
      return isNaN(n) ? 0 : n;
    }

    var shown = readCount();
    var pending = null;
    var checkTimer = 0;

    function drawerOpen() {
      var d = document.querySelector('cart-drawer');
      return !!(d && d.classList.contains('active'));
    }

    function burst(target) {
      var rect = target.getBoundingClientRect();
      if (!rect.width) return;
      var layer = document.createElement('div');
      layer.className = 'als-cart-burst';
      layer.setAttribute('aria-hidden', 'true');
      layer.style.left = rect.left + rect.width / 2 + 'px';
      layer.style.top = rect.top + rect.height / 2 + 'px';
      var html = '<span class="als-cart-burst__ring"></span>';
      for (var i = 0; i < 8; i++) {
        html += '<span class="als-cart-burst__spark" style="--a:' + (i * 45 + 22) + 'deg;--d:' + (i % 2 ? 22 : 30) + 'px"></span>';
      }
      layer.innerHTML = html;
      document.body.appendChild(layer);
      window.setTimeout(function () { layer.remove(); }, 1000);
    }

    function play(from, to) {
      var current = document.getElementById('cart-icon-bubble');
      if (!current) return;
      shown = to;
      if (REDUCED) return;

      current.classList.remove('als-cart-receive');
      void current.offsetWidth;
      current.classList.add('als-cart-receive');
      window.setTimeout(function () { current.classList.remove('als-cart-receive'); }, 900);
      burst(current);

      var num = current.querySelector('.cart-count-bubble span[aria-hidden="true"]');
      if (!num || from === to) return;
      var dir = to > from ? '' : ' is-down';
      num.classList.add('als-roll');
      num.innerHTML =
        '<span class="als-roll__out' + dir + '">' + from + '</span>' +
        '<span class="als-roll__in' + dir + '">' + to + '</span>';
      window.setTimeout(function () {
        if (!num.isConnected || !num.classList.contains('als-roll')) return;
        num.classList.remove('als-roll');
        num.textContent = String(to);
      }, 720);
    }

    // Dawn re-renders the icon a tick before it opens the cart drawer, so
    // decide whether to play now or after the drawer closes slightly later.
    function onChange() {
      window.clearTimeout(checkTimer);
      checkTimer = window.setTimeout(function () {
        var next = readCount();
        if (next === shown) return;
        if (drawerOpen()) {
          if (!pending) pending = { from: shown };
          return;
        }
        play(shown, next);
      }, 140);
    }

    new MutationObserver(onChange).observe(icon.parentNode, { childList: true, subtree: true, characterData: true });

    customElements.whenDefined('cart-drawer').then(function () {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      new MutationObserver(function () {
        if (drawer.classList.contains('active')) return;
        var next = readCount();
        if (pending || next !== shown) {
          var from = pending ? pending.from : shown;
          pending = null;
          window.setTimeout(function () { play(from, readCount()); }, 380);
        }
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     5. EDITORIAL NAVIGATION DRAWER
     Adds index numerals + label wrappers for the staggered reveal.
  ───────────────────────────────────────────────────────────── */
  function initNavDrawer() {
    var drawer = document.getElementById('menu-drawer');
    if (!drawer || drawer.dataset.alsEditorial) return;
    drawer.dataset.alsEditorial = '1';
    drawer.classList.add('als-editorial-drawer');

    var reveal = 0;
    var tabs = drawer.querySelector('.sultan-drawer__tabs');
    if (tabs) tabs.style.setProperty('--i', reveal++);

    var topItems = drawer.querySelectorAll('.menu-drawer__menu.has-submenu > li');
    topItems.forEach(function (li, index) {
      li.style.setProperty('--i', reveal++);
      li.classList.add('als-nav-item');
      var link = li.querySelector(':scope > a.menu-drawer__menu-item, :scope > details > summary.menu-drawer__menu-item');
      if (!link || link.querySelector('.als-nav-idx')) return;

      for (var i = 0; i < link.childNodes.length; i++) {
        var node = link.childNodes[i];
        if (node.nodeType === 3 && node.textContent.trim()) {
          var label = document.createElement('span');
          label.className = 'als-nav-label';
          label.textContent = node.textContent.trim();
          link.replaceChild(label, node);
          break;
        }
      }

      var idx = document.createElement('span');
      idx.className = 'als-nav-idx';
      idx.setAttribute('aria-hidden', 'true');
      idx.textContent = (index + 1 < 10 ? '0' : '') + (index + 1);
      link.insertBefore(idx, link.firstChild);
    });

    drawer.querySelectorAll('.menu-drawer__utility-links, .sultan-drawer__close').forEach(function (el) {
      el.style.setProperty('--i', reveal++);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     SHARED: run a frame callback only while an element is near
     the viewport. mode 'scroll' runs on scroll/resize; mode 'loop'
     runs every frame (for content that moves by itself).
  ───────────────────────────────────────────────────────────── */
  function whileInView(el, fn, mode) {
    var inView = false;
    var raf = 0;
    function frame() {
      raf = 0;
      if (!inView) return;
      fn();
      if (mode === 'loop' && !document.hidden) raf = requestAnimationFrame(frame);
    }
    function request() {
      if (inView && !raf) raf = requestAnimationFrame(frame);
    }
    new IntersectionObserver(function (entries) {
      inView = entries[entries.length - 1].isIntersecting;
      request();
    }, { rootMargin: '20% 0px' }).observe(el);
    if (mode !== 'loop') {
      window.addEventListener('scroll', request, { passive: true });
      window.addEventListener('resize', request, { passive: true });
    }
    document.addEventListener('visibilitychange', request);
    return request;
  }

  // 0 when the element's top is at the bottom of the screen, 1 once it has
  // travelled `span` of the viewport height upward.
  function enterProgress(rect, span) {
    var vh = window.innerHeight;
    return clamp((vh - rect.top) / (vh * span), 0, 1);
  }

  /* ─────────────────────────────────────────────────────────────
     6. CUSTOMER STORIES — the deck rises out of the page as the
     section scrolls in (card positions are set by the slider).
  ───────────────────────────────────────────────────────────── */
  function initStoryDeck() {
    if (REDUCED) return;
    document.querySelectorAll('testimonials-slider').forEach(function (ts) {
      var stack = ts.querySelector('.ts__story-stack--quote');
      if (!stack || stack.dataset.alsDeck) return;
      stack.dataset.alsDeck = '1';
      whileInView(stack, function () {
        var p = enterProgress(stack.getBoundingClientRect(), 0.75);
        stack.style.setProperty('--ts-enter', (1 - smoothstep(p)).toFixed(3));
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     7. INSTAGRAM — floating editorial frames
     The app owns its carousel; we only add depth on top: frames
     rise from alternating depths as the section enters, lean like
     a curved gallery wall by their position, drift in opposing
     parallax while the page scrolls, and tip back on exit.
  ───────────────────────────────────────────────────────────── */
  function initInstagram() {
    if (REDUCED) return;
    document.querySelectorAll('.gfa-widget').forEach(function (widget) {
      var section = widget.closest('.shopify-section') || widget;
      if (section.dataset.alsIg) return;
      section.dataset.alsIg = '1';

      function setup() {
        var viewport = widget.querySelector('.gfa-carousel__viewport');
        var slides = widget.querySelectorAll('.gfa-carousel__slide');
        if (!viewport || !slides.length) return false;
        section.classList.add('als-ig');

        whileInView(section, function () {
          var vh = window.innerHeight;
          var sRect = section.getBoundingClientRect();
          var vRect = viewport.getBoundingClientRect();
          var enter = enterProgress(sRect, 0.8);
          var exit = clamp(-sRect.top / Math.max(sRect.height, 1), 0, 1);
          var centre = clamp((sRect.top + sRect.height / 2 - vh / 2) / vh, -1, 1);

          var list = widget.querySelectorAll('.gfa-carousel__slide');
          var rects = [];
          for (var i = 0; i < list.length; i++) rects.push(list[i].getBoundingClientRect());
          // Phase 7: written after the reads above so each frame restyles once.
          viewport.style.setProperty('--ig-exit', exit.toFixed(3));
          var mid = vRect.left + vRect.width / 2;
          for (var j = 0; j < list.length; j++) {
            var r = rects[j];
            if (r.right < vRect.left - r.width || r.left > vRect.right + r.width) continue;
            var media = list[j].querySelector('.gfa-carousel__slide-media') || list[j];
            var p = clamp((r.left + r.width / 2 - mid) / vRect.width, -1, 1);
            var stagger = (j % 2) * 0.16;
            var inn = smoothstep(clamp((enter - stagger) / (1 - stagger), 0, 1));
            var s = media.style;
            s.setProperty('--ig-p', p.toFixed(3));
            s.setProperty('--ig-ap', Math.abs(p).toFixed(3));
            s.setProperty('--ig-in', inn.toFixed(3));
            s.setProperty('--ig-y', ((j % 2 ? 1 : -1) * centre * 16).toFixed(1) + 'px');
            s.setProperty('--ig-depth', (j % 2 ? 90 : 40) + 'px');
          }
        }, 'loop');
        return true;
      }

      if (!setup()) {
        var mo = new MutationObserver(function () {
          if (setup()) mo.disconnect();
        });
        mo.observe(widget, { childList: true, subtree: true });
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     8. GET UPDATE — a gilded, cinematic moment
     Purely presentational: the form, its submission, the welcome
     discount redirect and the success message are left untouched.
  ───────────────────────────────────────────────────────────── */
  function initNewsletter() {
    document.querySelectorAll('main .newsletter').forEach(function (nl) {
      if (nl.dataset.alsNl) return;
      nl.dataset.alsNl = '1';
      nl.classList.add('als-nl');
      if (REDUCED) {
        nl.classList.add('is-in');
        return;
      }

      var heading = nl.querySelector('.newsletter__wrapper > h2, .newsletter__wrapper > .h1');
      if (heading && !heading.dataset.alsSplit) {
        var text = heading.textContent.trim();
        heading.dataset.alsSplit = '1';
        heading.setAttribute('aria-label', text);
        heading.innerHTML = text.split('').map(function (ch, i) {
          return ch === ' '
            ? '<span class="als-nl-space" aria-hidden="true"> </span>'
            : '<span class="als-nl-char" aria-hidden="true" style="--ci:' + i + '">' + ch + '</span>';
        }).join('');
      }

      var wrapper = nl.querySelector('.newsletter__wrapper') || nl;
      new IntersectionObserver(function (entries) {
        var e = entries[entries.length - 1];
        nl.classList.toggle('is-live', e.isIntersecting);
        if (e.isIntersecting && e.intersectionRatio > 0.25) nl.classList.add('is-in');
      }, { threshold: [0, 0.25] }).observe(wrapper);

      whileInView(wrapper, function () {
        var p = enterProgress(wrapper.getBoundingClientRect(), 0.7);
        nl.style.setProperty('--nl-tilt', (1 - smoothstep(p)).toFixed(3));
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     9. OUR BRANDS — curved exhibition wall
     Phones: logos drift (opposite to the product rails) around a
     shallow curved wall; the one at centre faces you flat and a
     band of gold light crosses each logo as it passes. Desktop:
     the grid unfolds in 3D on entry and each logo tilts toward
     the pointer.
  ───────────────────────────────────────────────────────────── */
  function initBrands() {
    document.querySelectorAll('.brands-grid-wrapper').forEach(function (wrap) {
      if (wrap.dataset.alsBrands) return;
      wrap.dataset.alsBrands = '1';
      wrap.classList.add('als-brands');
      var grid = wrap.querySelector('.brands-grid');
      if (!grid) return;
      var cards = Array.prototype.slice.call(grid.querySelectorAll(':scope > .brand-card'));
      cards.forEach(function (c, i) { c.style.setProperty('--bi', i); });

      if (REDUCED) {
        wrap.classList.add('is-in');
        return;
      }

      new IntersectionObserver(function (entries, io) {
        if (entries[entries.length - 1].isIntersecting) {
          wrap.classList.add('is-in');
          io.disconnect();
        }
      }, { threshold: 0.2 }).observe(wrap);

      var mobile = window.matchMedia('(max-width: 767px)');
      var fine = window.matchMedia('(hover: hover) and (pointer: fine)');

      if (mobile.matches && window.AlsDriftRail && cards.length > 2) {
        var stage = document.createElement('div');
        stage.className = 'als-brands-stage';
        var track = document.createElement('div');
        track.className = 'als-brands-track';
        cards.forEach(function (c) { track.appendChild(c); });
        var clone = track.cloneNode(true);
        clone.classList.add('als-brands-track--clone');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, [tabindex]').forEach(function (a) { a.setAttribute('tabindex', '-1'); });
        stage.appendChild(track);
        stage.appendChild(clone);
        grid.appendChild(stage);
        grid.classList.add('is-wall');

        var rail = new window.AlsDriftRail({
          viewport: grid,
          stage: stage,
          speed: -30,
          getItems: function () { return stage.querySelectorAll('.brand-card'); },
          getLoopWidth: function () { return clone.offsetLeft - track.offsetLeft; },
        });
        rail.start({ reset: true });
      } else if (fine.matches) {
        cards.forEach(function (card) {
          card.addEventListener('pointermove', function (e) {
            var r = card.getBoundingClientRect();
            card.style.setProperty('--tx', (((e.clientX - r.left) / r.width) - 0.5).toFixed(3));
            card.style.setProperty('--ty', (((e.clientY - r.top) / r.height) - 0.5).toFixed(3));
            card.classList.add('is-tilting');
          });
          card.addEventListener('pointerleave', function () {
            card.style.setProperty('--tx', '0');
            card.style.setProperty('--ty', '0');
            card.classList.remove('is-tilting');
          });
        });
      }
    });
  }

  /* ─────────────────────────────────────────────────────────────
     BOOT
  ───────────────────────────────────────────────────────────── */
  function boot() {
    initWhyNumerals();
    initTicker();
    initHeaderIcons();
    initNavDrawer();
    initStoryDeck();
    initInstagram();
    initNewsletter();
    initBrands();
  }

  // This file is deferred in <head>, so it runs before the section
  // components (deferred later in <body>) are defined; wait for them.
  if (document.readyState === 'complete') {
    boot();
  } else {
    document.addEventListener('DOMContentLoaded', boot);
    window.addEventListener('load', boot);
  }

  document.addEventListener('shopify:section:load', boot);
})();
